import React, { useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Paper, Typography } from '@mui/material';
import Loader from '../../components/Loader';
import Form from '../../components/general/form';
import { ButtonContinue } from '../../components/general/mini_components';
import { EnhancedTable } from '../../components/general/EnhancedTable';
import { getDeletedPatientsService, restorePatientService } from '../../services';
import { decryptSinglePatientData } from '../../utils/index.jsx';
import { buildDedupIndex, findDuplicatePatient } from '../../utils/patientDedup';
import { filterPatients } from '../../workers/patientFilterLogic';
import { resolvePersonalFieldRoles } from '../../utils/personalFieldRoles';
import { useReasonPrompt, ReasonModal } from './patient/ReasonPromptModal';
import { MIN_REASON_LENGTH } from './patient/toolbar';
import DuplicatePatientModal from './AddPatient/DuplicatePatientModal';

interface Props {
    investigation: any;
    personalFields: any[];
    // Same field definition the "By Personal details" tab builds, so both tabs are
    // searched the same way — the difference is only which set is being filtered.
    personalFieldsForm: any;
    // TabPanel keeps every panel mounted (hidden={value !== index}), so this component
    // does NOT remount when the user comes back to the tab. Without knowing when it
    // becomes visible it would keep showing whatever it fetched on first mount — a
    // patient deleted since then would simply never appear.
    isActive: boolean;
    canRestore: boolean;
    // The live census, used only to warn about duplicates at restore time.
    activePatients?: any[];
}

/**
 * SHER-723 — the investigation's deleted patients, and restoring one.
 *
 * This tab cannot reuse the active-search machinery. Deleted enrolments are evicted from
 * Redis on delete and never reach the client's synced census, so the list is fetched from
 * the API on mount. The server cannot filter it either — personal data is ciphertext —
 * so the whole set arrives and is decrypted here, exactly as the live census is.
 */
const DeletedPatientsTab: React.FC<Props> = ({ investigation, personalFields, personalFieldsForm, isActive, canRestore, activePatients }) => {
    const [loading, setLoading] = useState(true);
    const [patients, setPatients] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [duplicateResult, setDuplicateResult] = useState<any>(null);
    // null = nothing searched yet. An empty object = "searched with no criteria",
    // which per the form's own note means "show everything".
    const [searchValues, setSearchValues] = useState<Record<string, string> | null>(null);
    const restorePrompt = useReasonPrompt();

    async function load() {
        setLoading(true);
        try {
            const response = await getDeletedPatientsService(investigation.uuid);
            setPatients((response.patients || []).map((patient: any) => ({
                ...patient,
                personalData: decryptSinglePatientData(patient.personalData, investigation),
            })));
            setError(null);
        } catch (err) {
            setError('pages.hospital.search-patient.deleted.restore.error');
        } finally {
            setLoading(false);
        }
    }

    // Refetch every time the tab becomes visible, not just on mount — see isActive.
    useEffect(() => {
        if (!isActive) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investigation.uuid, isActive]);

    // Built from the LIVE census on purpose: the point is to catch the case where the
    // patient was re-registered by hand while this record was deleted, which would leave
    // two active enrolments for one person once this one comes back.
    const phoneFieldNames = useMemo(() => resolvePersonalFieldRoles(personalFields).phone, [personalFields]);
    const idCardFieldNames = useMemo(() => resolvePersonalFieldRoles(personalFields).idCard, [personalFields]);
    const dedupIndex = useMemo(
        () => buildDedupIndex(activePatients as any, phoneFieldNames, idCardFieldNames),
        [activePatients, phoneFieldNames, idCardFieldNames]
    );

    // EnhancedTable hands the action func `row.id`, not the row index — and row.id here
    // is patientIdInvestigation, the tenant-facing patient number.
    function askRestore(patientIdInvestigation: number) {
        const index = patients.findIndex((p) => p.id === patientIdInvestigation);
        const candidate = patients[index];
        if (!candidate) return;
        const duplicate = findDuplicatePatient(dedupIndex, candidate?.personalData, phoneFieldNames, idCardFieldNames);
        if (duplicate) {
            // Warn, never block — the server is structurally blind to this, and a real
            // duplicate may still be the right thing to restore.
            setDuplicateResult({ ...duplicate, pendingIndex: index });
            return;
        }
        restorePrompt.open(index);
    }

    function confirmRestore() {
        const patient = patients[restorePrompt.pendingIndex as number];
        if (!patient) return;
        restorePrompt.submit({
            requiredKey: 'pages.hospital.search-patient.deleted.restore.reason_required',
            minLength: MIN_REASON_LENGTH,
            tooShortKey: 'hospital.patient.delete.reason_too_short_client',
            action: (reason) => restorePatientService(investigation.uuid, patient.uuid, reason).then(load),
            resolveError: (err) => err?.response?.data?.errorMessage
                || 'pages.hospital.search-patient.deleted.restore.error',
        });
    }

    // Filtering is client-side for the same reason the live search is: the API only ever
    // sees ciphertext and cannot match on a name.
    //
    // It goes through filterPatients — the same pure function the active-search worker
    // uses — rather than a local loop. A hand-rolled `includes()` looked equivalent and is
    // not: it silently drops trimSearchValues, exact-match on option fields, the ±legacy
    // birthdate comparison (SHER-685: a ms-epoch DOB can never substring-match
    // YYYY-MM-DD) and digit-only phone matching (SHER-579: "77 289 86 43" vs
    // "772898643"). Both were bugs already fixed once; re-deriving the filter here
    // re-introduced them, and only on this tab, so the same query would find a patient
    // next door and miss it here.
    const visible = useMemo(() => {
        if (searchValues === null) return [];
        return filterPatients({ patients, searchValues, personalFields }).patients;
    }, [patients, searchValues, personalFields]);

    if (loading) {
        return <Loader />;
    }

    const headCells = [
        { id: 'id', alignment: 'left', label: <Translate id="investigation.create.personal_data.fields.id" /> },
        { id: 'name', alignment: 'left', label: <Translate id="investigation.create.personal_data.fields.name" /> },
        { id: 'surnames', alignment: 'left', label: <Translate id="investigation.create.personal_data.fields.surnames" /> },
        { id: 'deletedAt', alignment: 'left', label: <Translate id="pages.hospital.search-patient.deleted.deleted-at" /> },
        { id: 'deletedBy', alignment: 'left', label: <Translate id="pages.hospital.search-patient.deleted.deleted-by" /> },
        { id: 'deletedReason', alignment: 'left', label: <Translate id="pages.hospital.search-patient.deleted.reason" /> },
    ];

    const rows = visible.map((patient, index) => ({
        id: patient.id,
        name: patient.personalData?.name ?? '',
        surnames: patient.personalData?.surnames ?? '',
        deletedAt: patient.deletedAt ? new Date(patient.deletedAt).toLocaleString() : '',
        deletedBy: patient.deletedBy ?? '',
        // Deletions from before the reason was mandatory carry the old default.
        deletedReason: patient.deletedReason ?? '',
        index,
    }));

    return (
        <Paper style={{ padding: '1rem', margin: '1rem' }}>
            {error && (
                <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
                    <Translate id={error} />
                </Typography>
            )}
            <Typography variant="body2" style={{ marginBottom: 8 }}>
                <Translate id="pages.hospital.search-patient.deleted.note" />
            </Typography>
            <Form fields={personalFieldsForm} fullWidth
                submitText="investigation.search-patients.search"
                callBackForm={(values: Record<string, string>) => setSearchValues(values || {})} />
            {searchValues !== null && visible.length === 0 && (
                <Typography variant="body2" style={{ marginTop: 16 }}>
                    <Translate id="pages.hospital.search-patient.deleted.empty" />
                </Typography>
            )}
            {searchValues !== null && visible.length > 0 && (
                <EnhancedTable
                    noHeader
                    noSelectable
                    rows={rows}
                    headCells={headCells}
                    actions={canRestore ? [{
                        // "undo" rather than a bespoke icon: IconGenerator has no
                        // "restore" type, and undo is what this actually is.
                        type: 'undo',
                        func: (patientIdInvestigation: number) => askRestore(patientIdInvestigation),
                    }] : []}
                />
            )}
            <ReasonModal
                prompt={restorePrompt}
                title={<Translate id="pages.hospital.search-patient.deleted.restore.title" />}
                body={<Translate id="pages.hospital.search-patient.deleted.restore.description" />}
                reasonLabel={<Translate id="pages.hospital.search-patient.deleted.restore.reason_label" />}
                confirmLabel="pages.hospital.search-patient.deleted.restore"
                onConfirm={confirmRestore}
            />
            {duplicateResult && (
                <DuplicatePatientModal
                    open
                    matches={duplicateResult.matches}
                    reasons={duplicateResult.reasons}
                    personalFields={personalFields}
                    closeModal={() => setDuplicateResult(null)}
                    onGoBack={() => setDuplicateResult(null)}
                    onReviewPatient={() => setDuplicateResult(null)}
                    onCreateAnyway={() => {
                        const index = duplicateResult.pendingIndex;
                        setDuplicateResult(null);
                        restorePrompt.open(index);
                    }}
                />
            )}
        </Paper>
    );
};

export default DeletedPatientsTab;
